import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  FEATURE_LABEL_KEYS,
  PLAN_LABEL_KEYS,
  THEME_LABEL_KEYS,
} from '@hapana/shared/constants/entitlements.constants'
import {
  UpgradeRequestProvider,
  type UpgradeRequest,
} from '@hapana/shared/web/components/shared/feature-gate/upgrade-request.context'

import { useSupportAssistant } from './support-assistant.hooks'

/**
 * The issue type an upgrade request is filed under.
 *
 * Its own type rather than STUDIO_BILLING_CONCERN, which is about invoices and payments on an
 * existing plan — not about changing the plan. Being distinct also gives GTM upgrade demand as a
 * funnel of its own: which locked feature was clicked, and how often.
 *
 * The value must exist in the `SupportIssueType` GraphQL enum (servers/ai-assistant) or submission
 * fails validation at the gateway, not in the form.
 */
const UPGRADE_ISSUE_TYPE = 'CHANGE_PLAN'

/**
 * Turns a click on a locked surface into a pre-filled support ticket.
 *
 * Mounted inside `SupportAssistantProvider` (it needs the sidebar setters) and above the app's
 * routes, so every lock rendering in Core — route gates, control-centre tiles, sidebar nav —
 * shares one behaviour and one wording.
 */
export const UpgradeRequestBridge: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  // 'common' is where FEATURE_/PLAN_/THEME_LABEL_KEYS resolve (see entitlements.hooks.ts, which
  // builds the very tooltip these locks show). Getting this wrong does not throw — i18next just
  // returns the key, so the ticket reads "Required: entitlements.plans.premium".
  const { t } = useTranslation('common')
  const { setIsOpen, setActivePanel, setActiveTab, setPrefillIssueType, setPrefillDescription } =
    useSupportAssistant()

  const handleRequestUpgrade = useCallback(
    ({ feature, checkResult, surfaceLabel }: UpgradeRequest) => {
      const info = checkResult.upgradeInfo
      const featureLabel = surfaceLabel || t(FEATURE_LABEL_KEYS[feature])

      // Name the tier AND the add-on, because they are different purchases. A theme feature can be
      // blocked by both at once (plan too low to even buy the add-on), and a request that mentions
      // only one sends the customer round the loop twice.
      const wants: string[] = []
      if (info?.requiredPlan) {
        wants.push(t(PLAN_LABEL_KEYS[info.requiredPlan]))
      }
      if (info?.requiredTheme) {
        wants.push(t(THEME_LABEL_KEYS[info.requiredTheme]))
      }

      const description = t('entitlements.upgradeRequest.description', {
        feature: featureLabel,
        featureKey: feature,
        required: wants.length
          ? wants.join(' + ')
          : t('entitlements.upgradeRequest.unknownRequirement'),
      })

      // Order matters: set the payload before opening, so the support tab's prefill effects see the
      // values on its first render rather than flashing an empty form.
      setPrefillIssueType(UPGRADE_ISSUE_TYPE)
      setPrefillDescription(description)
      setActiveTab('support')
      setActivePanel('assistant')
      setIsOpen(true)
    },
    [t, setPrefillIssueType, setPrefillDescription, setActiveTab, setActivePanel, setIsOpen]
  )

  return (
    <UpgradeRequestProvider onRequestUpgrade={handleRequestUpgrade}>
      {children}
    </UpgradeRequestProvider>
  )
}
